// =============================================================================
// app/(tabs)/chat.tsx — the Chat tab (DM list + Communities + Live segments).
//
// MIGRATION (bare RN, P3 DMs / blueprint §2): ported off apps/cryb-native
// app/(tabs)/chat.tsx. Swaps: expo-router useRouter → react-navigation
// useNavigation (router.push('/dm/:id') → navigate('DM',{id}); '/dm/new' →
// navigate('DMNew'); '/community/:name' → navigate('Community',{name});
// '/voice/:id' → navigate('VoiceRoom',{id}); '/communities' → navigate('Communities');
// '/servers/discover' → navigate('ServersDiscover')). @expo/vector-icons → ../
// components/ui/Icon. The live DM-unread store wiring (setDmUnread/bumpDmUnread/
// clearDmUnread/reconcileDmUnread + onDmUnreadChange), the real-time preview bump,
// cross-device read sync, and reconnect reconcile are PRESERVED VERBATIM.
//
//   GET /direct-messages/conversations — DM list
//   GET /communities                    — joined communities (Communities segment)
//   GET /voice/rooms                    — active voice rooms (Live segment)
// =============================================================================
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  FlatList,
  TouchableOpacity,
  TextInput,
  RefreshControl,
  ActivityIndicator,
  Image,
  useWindowDimensions,
  LayoutChangeEvent,
} from 'react-native';
import LinearGradient from 'react-native-linear-gradient';
import Animated, { useAnimatedStyle, useSharedValue, withSpring } from 'react-native-reanimated';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '../../components/ui/Icon';
import type { RootStackParamList } from '../../navigation/types';
import { colors, spacing, radius, fonts, typographyScale, shadowTokens, getLayout, layout as layoutTokens } from '../../constants/theme';
import { api, isAuthExpired } from '../../services/api';
import {
  connectSocket,
  onDmMessage,
  onDmRead,
  onReconnect,
  fetchUnreadCounts,
  type DmMessagePayload,
  type DmReadPayload,
} from '../../services/socket';
import {
  setDmUnread,
  bumpDmUnread,
  clearDmUnread,
  reconcileDmUnread,
  getDmUnreadFor,
  onDmUnreadChange,
  startDmOutbox,
} from '../../services/dmState';
import ParticleBackground from '../../components/ui/ParticleBackground';
import Asset3D from '../../components/ui/Asset3D';
import ScreenHeading from '../../components/ui/ScreenHeading';
import ScreenTip from '../../components/ui/ScreenTip';
import { GlassCard, GlassButton, GlassSurface } from '../../components/glass';
import { usePresence } from '../../services/presence';
import { tabularNums } from '../../lib/format';

// The Chat tab can jump to flat root-stack screens (DM/DMNew/Community/etc), so
// type the navigator against the RootStack param list.
type Nav = NativeStackNavigationProp<RootStackParamList>;

const SEGMENTS = ['Messages', 'Communities', 'Live'] as const;

interface Conversation {
  id: string;
  other_user: {
    id?: string;
    display_name: string;
    username: string;
    avatar_url?: string;
    isVerified?: boolean;
  };
  last_message?: {
    content: string;
    created_at: string;
    // WhatsApp preview enrichers (additive, derived from the raw lastMessage —
    // no extra fetch): whose message it was (→ "You: " prefix) and whether it
    // was a media attachment (→ "📷 Photo" / "🎤 Voice") when there's no text.
    sender_id?: string;
    media_kind?: 'image' | 'voice' | 'file';
  };
  unread_count: number;
}

// 'Communities' segment: a joined community the user can tap into (→ Community/:name).
interface CommunityRow {
  name: string;
  displayName: string;
  avatar?: string;
  memberCount: number;
}

// 'Live' segment: an active voice room (parsed from the RAW LiveKit shape, see
// normalizeVoiceRoom — the useful values live in the JSON-string `metadata`).
interface VoiceRow {
  id: string;
  title: string;
  topic?: string;
  participants_count: number;
}

// GET /voice/rooms returns RAW LiveKit rooms; the real id/name/topic are in the
// JSON-string `metadata`. Parse defensively (it may be missing/malformed).
function normalizeVoiceRoom(raw: any): VoiceRow {
  let meta: any = {};
  if (raw?.metadata && typeof raw.metadata === 'string') {
    try { meta = JSON.parse(raw.metadata) ?? {}; } catch { meta = {}; }
  }
  const derivedId = typeof raw?.name === 'string' ? raw.name.replace(/^custom_room_/, '') : '';
  return {
    id: meta?.roomId || derivedId || raw?.sid || '',
    title: meta?.name || 'Voice Room',
    topic: typeof meta?.topic === 'string' ? meta.topic : undefined,
    participants_count: typeof raw?.numParticipants === 'number' ? raw.numParticipants : 0,
  };
}

// Backend conversation shape: { id, participants:[{id,username,displayName,
// avatar,isVerified}], lastMessage:{content,createdAt,...} | null, messageCount,
// lastReadMessageId }. There is no other_user/last_message/unread_count, so
// derive the client shape from the other participant + lastMessage.
// Derive a WhatsApp media label from a raw message's attachments. The server
// relation is `MessageAttachment` (camelCase) or `attachments`; the useful bit
// is `contentType` ('image/*', 'audio/*'). Returns undefined for text-only.
function mediaKindOf(rawMsg: any): 'image' | 'voice' | 'file' | undefined {
  const list = Array.isArray(rawMsg?.MessageAttachment)
    ? rawMsg.MessageAttachment
    : Array.isArray(rawMsg?.attachments)
      ? rawMsg.attachments
      : [];
  if (!list.length) return undefined;
  const ct = String(list[0]?.contentType ?? '').toLowerCase();
  if (ct.startsWith('image/')) return 'image';
  if (ct.startsWith('audio/')) return 'voice';
  return 'file';
}

function normalizeConversation(raw: any): Conversation {
  const other = Array.isArray(raw?.participants) ? raw.participants[0] : null;
  const last = raw?.lastMessage;
  return {
    id: raw?.id,
    other_user: {
      id: other?.id ?? other?.userId ?? undefined,
      display_name: other?.displayName ?? other?.display_name ?? '',
      username: other?.username ?? 'user',
      avatar_url: other?.avatar ?? other?.avatar_url,
      isVerified: other?.isVerified ?? false,
    },
    last_message: last
      ? {
          content: last.content ?? '',
          // Backend message timestamp is `timestamp` (not createdAt) — read it
          // first so the conversation time label renders.
          created_at: last.timestamp ?? last.createdAt ?? last.created_at ?? '',
          sender_id: last.userId ?? last.sender_id ?? last.User?.id ?? undefined,
          media_kind: mediaKindOf(last),
        }
      : undefined,
    unread_count: raw?.unreadCount ?? raw?.unread_count ?? 0,
  };
}

// Legacy relative label — kept as a fallback for invalid dates.
function relativeLabel(diff: number): string {
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d`;
  return `${Math.floor(days / 7)}w`;
}

// WhatsApp-style timestamp: <1min 'now'; same day → 'HH:mm'; yesterday →
// 'Yesterday'; <7d → weekday short ('Mon'); else 'DD/MM/YY'. Falls back to the
// legacy relative label if the date is invalid.
function whatsAppTime(dateStr?: string): string {
  if (!dateStr) return '';
  const then = new Date(dateStr);
  const ts = then.getTime();
  if (Number.isNaN(ts)) return relativeLabel(Date.now() - ts);
  const now = new Date();
  const diff = now.getTime() - ts;
  if (diff < 60000) return 'now';
  const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const dayDelta = Math.round((startOfDay(now) - startOfDay(then)) / 86400000);
  if (dayDelta <= 0) {
    return then.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  if (dayDelta === 1) return 'Yesterday';
  if (dayDelta < 7) return then.toLocaleDateString([], { weekday: 'short' });
  const yy = String(then.getFullYear()).slice(-2);
  const mm = String(then.getMonth() + 1).padStart(2, '0');
  const dd = String(then.getDate()).padStart(2, '0');
  return `${dd}/${mm}/${yy}`;
}

// Never render raw ciphertext. If the preview looks encrypted (e2e: prefix or a
// long base64-ish blob), report it as encrypted so the row can show a lock glyph.
function isEncryptedPreview(content?: string): boolean {
  if (!content) return false;
  const c = content.trim();
  if (c.startsWith('e2e:')) return true;
  return /^[A-Za-z0-9+/=]{24,}$/.test(c);
}

function ConversationRow({ convo, onPress, meId }: { convo: Conversation; onPress: () => void; meId?: string | null }) {
  const user = convo.other_user || { display_name: 'User', username: 'user' };
  const hasUnread = (convo.unread_count ?? 0) > 0;
  const displayName = user.display_name || user.username || 'User';
  const initial = (displayName.charAt(0) || 'U').toUpperCase();
  // Live presence: lights an online dot on the avatar and shows "typing…" in the
  // preview line when the peer is actively typing in THIS conversation.
  const presence = usePresence(user.id);
  const isOnline = !!presence?.online;
  const isTyping = !!presence?.typingIn && presence.typingIn === convo.id;

  const last = convo.last_message;
  const rawPreview = last?.content || '';
  const encrypted = !isTyping && isEncryptedPreview(rawPreview);
  const isVerified = !!user.isVerified;
  // WhatsApp preview: "You: " when the last message is mine; a media glyph when
  // it was a photo/voice attachment with no accompanying text.
  const mine = !!meId && !!last?.sender_id && last.sender_id === meId;
  const mediaLabel =
    last?.media_kind === 'image' ? '📷 Photo'
    : last?.media_kind === 'voice' ? '🎤 Voice message'
    : last?.media_kind === 'file' ? '📎 Attachment'
    : '';
  // Text preview: media label when there's no text body, else the content.
  const previewBody = rawPreview.trim() ? rawPreview : mediaLabel;

  // Avatar block — a 2px primary ring when there's unread (WhatsApp emphasis).
  const avatarInner = (
    <View>
      {user.avatar_url ? (
        <Image source={{ uri: user.avatar_url }} style={styles.convoAvatar} />
      ) : (
        <View style={[styles.convoAvatar, styles.convoAvatarFallback]}>
          <Text style={styles.convoAvatarLetter}>{initial}</Text>
        </View>
      )}
      {isOnline && <View style={styles.onlineDot} />}
    </View>
  );

  return (
    <TouchableOpacity
      style={styles.convoRow}
      activeOpacity={0.6}
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={`Open conversation with ${displayName}${isOnline ? ', online' : ''}`}
    >
      {hasUnread ? <View style={styles.avatarRing}>{avatarInner}</View> : avatarInner}
      <View style={styles.convoContent}>
        <View style={styles.convoTopRow}>
          <View style={styles.convoNameWrap}>
            <Text
              style={[styles.convoName, hasUnread && styles.convoNameUnread]}
              numberOfLines={1}
            >
              {displayName}
            </Text>
            {isVerified && (
              <Ionicons
                name="checkmark-circle"
                size={14}
                color={colors.primary}
                style={styles.verifiedIcon}
              />
            )}
          </View>
          <Text style={[styles.convoTime, hasUnread && styles.convoTimeUnread]} numberOfLines={1}>
            {whatsAppTime(convo.last_message?.created_at)}
          </Text>
        </View>
        <View style={styles.convoBottomRow}>
          {isTyping ? (
            <Text style={[styles.convoMessage, styles.convoTyping]} numberOfLines={1}>
              typing…
            </Text>
          ) : encrypted ? (
            <View style={styles.previewLockRow}>
              <Ionicons name="lock-closed" size={13} color={colors.muted} />
              <Text
                style={[styles.convoMessage, styles.previewLockText, hasUnread && styles.convoMessageUnread]}
                numberOfLines={1}
              >
                Encrypted message
              </Text>
            </View>
          ) : (
            <Text
              style={[styles.convoMessage, hasUnread && styles.convoMessageUnread]}
              numberOfLines={1}
            >
              {mine && previewBody ? <Text style={styles.previewYou}>You: </Text> : null}
              {previewBody || 'No messages'}
            </Text>
          )}
          {hasUnread && (
            <View style={styles.unreadBadge}>
              <Text style={styles.unreadText}>
                {(convo.unread_count ?? 0) > 99 ? '99+' : convo.unread_count}
              </Text>
            </View>
          )}
        </View>
      </View>
    </TouchableOpacity>
  );
}

// WhatsApp-style inset hairline between DM rows (aligns to text, not the avatar).
function RowSeparator() {
  return <View style={styles.rowSeparator} />;
}

export default function ChatScreen() {
  const navigation = useNavigation<Nav>();
  // Safe-area top inset for the self-owned header (P0: title under the clock).
  const insets = useSafeAreaInsets();
  const { width } = useWindowDimensions();
  const layout = getLayout(width);
  const containerStyle = layout !== 'phone' ? { maxWidth: layout === 'desktop' ? 720 : 620, alignSelf: 'center' as const, width: '100%' as const } : undefined;
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);
  // A failed DM-list load shows an inline error + Retry (NOT the "No messages yet"
  // empty state, which made a transient network error look like an empty inbox).
  const [dmError, setDmError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [activeSegment, setActiveSegment] = useState<typeof SEGMENTS[number]>('Messages');
  const [search, setSearch] = useState('');
  // Per-segment data (lazily loaded when the segment is first opened).
  const [communities, setCommunities] = useState<CommunityRow[]>([]);
  const [voiceRooms, setVoiceRooms] = useState<VoiceRow[]>([]);
  const [segLoading, setSegLoading] = useState(false);

  // Segmented control: measure the track so the active pill can slide on a
  // critically-damped spring (skill §4) instead of cross-fading.
  const [segTrackWidth, setSegTrackWidth] = useState(0);
  const segIndex = SEGMENTS.indexOf(activeSegment);
  const pillX = useSharedValue(0);
  const segPad = 3; // matches the glass pill contentStyle padding
  const segItemWidth = segTrackWidth > 0 ? (segTrackWidth - segPad * 2) / SEGMENTS.length : 0;
  useEffect(() => {
    pillX.value = withSpring(segIndex * segItemWidth, { damping: 18, stiffness: 200 });
  }, [segIndex, segItemWidth, pillX]);
  const pillStyle = useAnimatedStyle(() => ({
    width: segItemWidth,
    transform: [{ translateX: pillX.value }],
  }));
  const onSegTrackLayout = useCallback((e: LayoutChangeEvent) => {
    setSegTrackWidth(e.nativeEvent.layout.width);
  }, []);

  const fetchConversations = useCallback(async () => {
    try {
      const res = await api<{ conversations?: any[] }>('/direct-messages/conversations');
      if (res.success && res.data) {
        const list = (res.data.conversations ?? []).map(normalizeConversation);
        setConversations(list);
        setDmError(null);
        // Seed the shared unread store from the server counts so the per-thread
        // badge + nav dot are correct on first load (and after pull-to-refresh).
        for (const c of list) setDmUnread(c.id, c.unread_count ?? 0);
      } else if (!res.success && !isAuthExpired(res)) {
        // isAuthExpired → the session just ended and api() already routed to Login;
        // do NOT surface an error on top of the login screen. Otherwise show an
        // inline error + Retry instead of falling through to the empty state (a
        // transient failure must not read as "you have no conversations").
        setDmError(res.error || 'Failed to load messages. Please try again.');
      }
    } catch {
      setDmError('Could not load messages. Check your connection and try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  // 'Communities' segment: GET /communities → { data: { items } }; show only joined.
  const fetchCommunities = useCallback(async () => {
    const res = await api<{ items?: any[] }>('/communities');
    if (res.success && res.data) {
      const items = Array.isArray(res.data.items) ? res.data.items : [];
      setCommunities(
        items
          .filter((c) => c?.isJoined)
          .map((c) => ({
            name: c?.name ?? '',
            displayName: c?.displayName ?? c?.name ?? '',
            avatar: c?.icon ?? c?.avatar,
            memberCount: c?.memberCount ?? c?._count?.CommunityMember ?? 0,
          }))
          .filter((c) => !!c.name)
      );
    }
  }, []);

  // 'Live' segment: GET /voice/rooms → { rooms } of RAW LiveKit rooms; normalize.
  const fetchVoiceRooms = useCallback(async () => {
    const res = await api<{ rooms?: any[] }>('/voice/rooms');
    if (res.success && res.data) {
      const raw = Array.isArray(res.data.rooms) ? res.data.rooms : [];
      setVoiceRooms(raw.map(normalizeVoiceRoom).filter((r) => !!r.id));
    }
  }, []);

  // Load the active segment's data. Messages loads on mount (below); the other
  // two load on first switch (and on pull-to-refresh / onRefresh).
  const loadSegment = useCallback(async (seg: typeof SEGMENTS[number]) => {
    if (seg === 'Communities') { setSegLoading(true); await fetchCommunities(); setSegLoading(false); }
    else if (seg === 'Live') { setSegLoading(true); await fetchVoiceRooms(); setSegLoading(false); }
  }, [fetchCommunities, fetchVoiceRooms]);

  const handleSegmentPress = useCallback((seg: typeof SEGMENTS[number]) => {
    setActiveSegment(seg);
    void loadSegment(seg);
  }, [loadSegment]);

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    if (activeSegment === 'Messages') await fetchConversations();
    else await loadSegment(activeSegment);
    setRefreshing(false);
  }, [activeSegment, fetchConversations, loadSegment]);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  // Refetch the DM list whenever the Chat tab regains focus. Without this, a
  // conversation just created elsewhere (DMNewScreen → send) doesn't appear when
  // you come back — the inbox kept showing "No messages yet" (#55). Only refetch
  // the Messages segment (the others load on switch / pull-to-refresh).
  useFocusEffect(
    useCallback(() => {
      if (activeSegment === 'Messages') fetchConversations();
    }, [activeSegment, fetchConversations])
  );

  // Resolve my user id once so the unread bump only fires for INBOUND (peer)
  // messages, not echoes of my own sends across my devices.
  const meIdRef = useRef<string | null>(null);
  // Mirror the resolved id into state so ConversationRow can reactively show the
  // "You: " preview prefix once it's known (the ref alone won't re-render rows).
  const [meId, setMeId] = useState<string | null>(null);
  useEffect(() => {
    let active = true;
    import('../../services/api')
      .then(({ getMe }) => getMe())
      .then((res: any) => {
        const uid = res?.data?.id ?? res?.data?.user?.id;
        if (active && uid) { meIdRef.current = uid; setMeId(uid); }
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  // Real-time preview bump (ADDITIVE): when a dm:message arrives, update the
  // matching conversation's last_message, float it to the top, and bump its
  // unread count in the shared store (which lights the per-thread badge + nav
  // dot) — unless the message is our own. If the conversation isn't in the list
  // yet, refresh from REST.
  useEffect(() => {
    connectSocket();
    const handler = (payload: DmMessagePayload) => {
      if (!payload?.conversationId || !payload.message) return;
      const msg: any = payload.message;
      const senderId = msg?.userId ?? msg?.sender_id ?? msg?.User?.id ?? null;
      const isMine = !!meIdRef.current && senderId === meIdRef.current;
      // Inbound peer message → bump unread (the DM screen clears it on open/read).
      if (!isMine) bumpDmUnread(payload.conversationId);
      setConversations((prev) => {
        const list = Array.isArray(prev) ? prev : [];
        const idx = list.findIndex((c) => c.id === payload.conversationId);
        if (idx === -1) {
          // Unknown conversation (e.g. brand new) — pull a fresh list.
          fetchConversations();
          return list;
        }
        const updated: Conversation = {
          ...list[idx],
          last_message: {
            content: msg?.content ?? '',
            created_at: msg?.timestamp ?? msg?.createdAt ?? msg?.created_at ?? new Date().toISOString(),
            sender_id: senderId ?? undefined,
            media_kind: mediaKindOf(msg),
          },
        };
        return [updated, ...list.filter((_, i) => i !== idx)];
      });
    };
    const unsubscribe = onDmMessage(handler);
    return () => unsubscribe();
  }, [fetchConversations]);

  // Cross-device read sync: when ANY of my devices marks a thread read, the
  // server echoes dm:read to my own room. Clear that thread's unread here so the
  // list badge + nav dot drop live on this device too — no refetch. (LR2)
  useEffect(() => {
    const unsub = onDmRead((payload: DmReadPayload) => {
      if (!payload?.conversationId) return;
      if (meIdRef.current && payload.readerId !== meIdRef.current) return;
      clearDmUnread(payload.conversationId);
    });
    return () => unsub();
  }, []);

  // Reconnect reconcile: on a non-recovered reconnect, pull authoritative unread
  // counts and overwrite the store (server is the source of truth for counts).
  useEffect(() => {
    const unsub = onReconnect(({ recovered }) => {
      if (recovered) return;
      fetchUnreadCounts()
        .then((counts) => {
          if (counts) reconcileDmUnread(counts.dmByConversation);
          // Re-pull the list so last-message previews + ordering are fresh too.
          fetchConversations();
        })
        .catch(() => {});
    });
    return () => unsub();
  }, [fetchConversations]);

  // Arm the app-global offline outbox (idempotent). This registers a single
  // reconnect→flush handler that drains queued sends for ANY conversation, so a
  // failed message flushes on reconnect even if the user never reopens that exact
  // thread. The per-thread flush in DMScreen stays for immediate UX; the shared
  // clientMessageId dedupe prevents any double-send between the two paths.
  useEffect(() => { startDmOutbox(); }, []);

  // Re-render the list when the shared unread store changes (badges live-update
  // even while we're on this screen and a message lands in a background thread).
  const [, setUnreadTick] = useState(0);
  useEffect(() => onDmUnreadChange(() => setUnreadTick((n) => n + 1)), []);

  const filtered = (Array.isArray(conversations) ? conversations : []).filter((c) => {
    if (!search) return true;
    const q = search.toLowerCase();
    const name = (c.other_user?.display_name || '').toLowerCase();
    const uname = (c.other_user?.username || '').toLowerCase();
    return name.includes(q) || uname.includes(q);
  });

  return (
    <View style={styles.container}>
      {/* Full-bleed particle field under the status bar (skill §12). */}
      <ParticleBackground />
      <ScreenTip
        tipKey="chat"
        title="Chat"
        text="Direct messages and community chats. Tap + to start a conversation."
      />
      {/* Translucent floating chrome — header + segmented control + search live
          in one glass stack; the list scrolls under it against the particle
          field (skill §12). Tabs has headerShown:false so this screen owns the
          header and pads below the iOS status bar. */}
      <View style={[styles.chromeStack, { paddingTop: insets.top + spacing.sm }, containerStyle]}>
        {/* Translucent floating bar (skill §12) — NOT an opaque strip. The list
            scrolls under it against the particle field. WhatsApp: title left,
            compose action right. */}
        <GlassSurface variant="bar" radius={radius.lg} contentStyle={styles.header}>
          <ScreenHeading kicker="Inbox" title="Chat" style={styles.headerHeading} />
          <TouchableOpacity
            onPress={() => navigation.navigate('DMNew')}
            accessibilityRole="button"
            accessibilityLabel="New message"
            hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
            style={styles.headerCompose}
          >
            <Ionicons name="create-outline" size={22} color={colors.primary} />
          </TouchableOpacity>
        </GlassSurface>

        {/* Segment Tabs — glass pill with a spring-slid active pill (skill §4). */}
        <GlassSurface
          variant="pill"
          radius={radius.full}
          contentStyle={styles.segmentTrack}
          style={styles.segmentGlass}
        >
          <View style={styles.segmentInner} onLayout={onSegTrackLayout}>
            {segItemWidth > 0 && (
              <Animated.View style={[styles.segmentActivePill, pillStyle]} pointerEvents="none" />
            )}
            {SEGMENTS.map((seg) => (
              <TouchableOpacity
                key={seg}
                style={styles.segmentTab}
                activeOpacity={0.7}
                onPress={() => handleSegmentPress(seg)}
                accessibilityRole="button"
                accessibilityLabel={seg}
                accessibilityState={{ selected: activeSegment === seg }}
              >
                <Text
                  style={[
                    styles.segmentText,
                    activeSegment === seg && styles.segmentTextActive,
                  ]}
                >
                  {seg}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        </GlassSurface>

        {/* Search (Messages only) */}
        {activeSegment === 'Messages' && (
          <GlassSurface
            variant="pill"
            radius={radius.full}
            contentStyle={styles.searchWrap}
            style={styles.searchGlass}
          >
            <Ionicons name="search-outline" size={16} color={colors.muted} />
            <TextInput
              style={styles.searchInput}
              placeholder="Search conversations..."
              placeholderTextColor={colors.muted}
              value={search}
              onChangeText={setSearch}
            />
          </GlassSurface>
        )}
      </View>

      {/* Scroll-edge fade where the list meets the glass chrome (skill §12) —
          soft gradient, not a hard 1px divider. */}
      <LinearGradient
        colors={[colors.bg, 'rgba(232,225,242,0)']}
        style={styles.scrollEdgeFade}
        pointerEvents="none"
      />

      {/* Messages segment — existing DM conversation list */}
      {activeSegment === 'Messages' && (
        loading ? (
          <View style={styles.loadingWrap}>
            <ActivityIndicator size="large" color={colors.primary} />
          </View>
        ) : dmError ? (
          // Inline error + Retry (mirrors DMScreen) — a failed load must NOT render
          // as the "No messages yet" empty state.
          <View style={styles.errorWrap}>
            <Asset3D name="error" size={120} style={{ marginBottom: spacing.sm }} />
            <Text style={styles.errorText}>{dmError}</Text>
            <TouchableOpacity
              style={styles.retryBtn}
              onPress={() => { setLoading(true); fetchConversations(); }}
              accessibilityRole="button"
              accessibilityLabel="Retry loading messages"
            >
              <Text style={styles.retryText}>Retry</Text>
            </TouchableOpacity>
          </View>
        ) : (
          <FlatList
            data={filtered}
            keyExtractor={(item, index) => item?.id ?? String(index)}
            initialNumToRender={15}
            maxToRenderPerBatch={12}
            windowSize={11}
            removeClippedSubviews
            renderItem={({ item }) => (
              <ConversationRow
                // Per-thread unread comes from the LIVE shared store (not the
                // stale REST snapshot) so a message landing while this list is
                // open bumps the badge instantly, and opening the thread clears
                // it here without a refetch.
                convo={{ ...item, unread_count: getDmUnreadFor(item.id) }}
                onPress={() => navigation.navigate('DM', { id: item.id })}
                meId={meId}
              />
            )}
            ItemSeparatorComponent={RowSeparator}
            ListEmptyComponent={
              <View style={styles.emptyWrap}>
                <GlassCard style={styles.emptyCard} padding={spacing.xl}>
                  <Asset3D name="chat" size={128} style={{ marginBottom: spacing.md }} />
                  <Text style={styles.emptyText}>No messages yet</Text>
                  <Text style={styles.emptySubtext}>Say hi to someone — your conversations will live here.</Text>
                  <GlassButton
                    title="Start a conversation"
                    icon="create-outline"
                    onPress={() => navigation.navigate('DMNew')}
                    fullWidth
                    style={styles.emptyCta}
                  />
                </GlassCard>
              </View>
            }
            refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={colors.primary} />}
            showsVerticalScrollIndicator={false}
            contentContainerStyle={[styles.listContent, containerStyle]}
          />
        )
      )}

      {/* Communities segment — the user's joined communities */}
      {activeSegment === 'Communities' && (
        segLoading ? (
          <View style={styles.loadingWrap}>
            <ActivityIndicator size="large" color={colors.primary} />
          </View>
        ) : (
          <FlatList
            data={Array.isArray(communities) ? communities : []}
            keyExtractor={(item, index) => item?.name ?? String(index)}
            renderItem={({ item }) => (
              <TouchableOpacity
                style={styles.convoRow}
                activeOpacity={0.6}
                onPress={() => { if (item.name) navigation.navigate('Community', { name: item.name }); }}
                accessibilityRole="button"
                accessibilityLabel={`Open community ${item.displayName || item.name}`}
              >
                {item.avatar ? (
                  <Image source={{ uri: item.avatar }} style={styles.convoAvatar} />
                ) : (
                  <View style={[styles.convoAvatar, styles.convoAvatarFallback]}>
                    <Text style={styles.convoAvatarLetter}>
                      {(item.displayName || item.name || 'C').charAt(0).toUpperCase()}
                    </Text>
                  </View>
                )}
                <View style={styles.convoContent}>
                  <Text style={styles.convoName} numberOfLines={1}>{item.displayName || item.name}</Text>
                  <Text style={styles.convoMessage} numberOfLines={1}>
                    c/{item.name} · {(item.memberCount ?? 0).toLocaleString()} members
                  </Text>
                </View>
                <Ionicons name="chevron-forward" size={18} color={colors.muted} />
              </TouchableOpacity>
            )}
            ListEmptyComponent={
              <View style={styles.emptyWrap}>
                <GlassCard style={styles.emptyCard} padding={spacing.xl}>
                  <Asset3D name="community" size={128} style={{ marginBottom: spacing.md }} />
                  <Text style={styles.emptyText}>No communities yet</Text>
                  <Text style={styles.emptySubtext}>Join a community to see its chats here.</Text>
                  <GlassButton
                    title="Discover Communities"
                    icon="compass-outline"
                    onPress={() => navigation.navigate('Communities')}
                    style={styles.emptyCta}
                  />
                  <TouchableOpacity
                    style={styles.discoverLink}
                    onPress={() => navigation.navigate('ServersDiscover')}
                    accessibilityRole="button"
                    accessibilityLabel="Discover Servers"
                  >
                    <Ionicons name="planet-outline" size={16} color={colors.primary} />
                    <Text style={styles.discoverLinkText}>Discover Servers</Text>
                  </TouchableOpacity>
                </GlassCard>
              </View>
            }
            ListFooterComponent={
              communities.length > 0 ? (
                <View>
                  <TouchableOpacity
                    style={styles.discoverLink}
                    onPress={() => navigation.navigate('Communities')}
                    accessibilityRole="button"
                    accessibilityLabel="Discover more communities"
                  >
                    <Ionicons name="compass-outline" size={16} color={colors.primary} />
                    <Text style={styles.discoverLinkText}>Discover more communities</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[styles.discoverLink, styles.discoverLinkTight]}
                    onPress={() => navigation.navigate('ServersDiscover')}
                    accessibilityRole="button"
                    accessibilityLabel="Discover Servers"
                  >
                    <Ionicons name="planet-outline" size={16} color={colors.primary} />
                    <Text style={styles.discoverLinkText}>Discover Servers</Text>
                  </TouchableOpacity>
                </View>
              ) : null
            }
            refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={colors.primary} />}
            showsVerticalScrollIndicator={false}
            contentContainerStyle={[styles.listContent, containerStyle]}
          />
        )
      )}

      {/* Live segment — active voice rooms */}
      {activeSegment === 'Live' && (
        segLoading ? (
          <View style={styles.loadingWrap}>
            <ActivityIndicator size="large" color={colors.primary} />
          </View>
        ) : (
          <FlatList
            data={Array.isArray(voiceRooms) ? voiceRooms : []}
            keyExtractor={(item, index) => item?.id ?? String(index)}
            renderItem={({ item }) => (
              <TouchableOpacity
                style={styles.convoRow}
                activeOpacity={0.6}
                onPress={() => { if (item.id) navigation.navigate('VoiceRoom', { id: item.id }); }}
                accessibilityRole="button"
                accessibilityLabel={`Join voice room ${item.title}`}
              >
                <View style={[styles.convoAvatar, styles.convoAvatarFallback]}>
                  <Ionicons name="mic" size={20} color={colors.primary} />
                </View>
                <View style={styles.convoContent}>
                  <View style={styles.convoTopRow}>
                    <Text style={styles.convoName} numberOfLines={1}>{item.title}</Text>
                    <View style={styles.liveBadge}>
                      <View style={styles.liveDot} />
                      <Text style={styles.liveText}>LIVE</Text>
                    </View>
                  </View>
                  <Text style={styles.convoMessage} numberOfLines={1}>
                    {item.topic ? `${item.topic} · ` : ''}{(item.participants_count ?? 0)} listening
                  </Text>
                </View>
              </TouchableOpacity>
            )}
            ListEmptyComponent={
              <View style={styles.emptyWrap}>
                <GlassCard style={styles.emptyCard} padding={spacing.xl}>
                  <Asset3D name="emptyCluster" size={128} style={{ marginBottom: spacing.md }} />
                  <Text style={styles.emptyText}>No live rooms</Text>
                  <Text style={styles.emptySubtext}>Active voice rooms will show up here.</Text>
                  <GlassButton
                    title="Start a Room"
                    icon="mic"
                    onPress={() => navigation.navigate('VoiceRooms')}
                    fullWidth
                    style={styles.emptyCta}
                  />
                </GlassCard>
              </View>
            }
            ListFooterComponent={
              voiceRooms.length > 0 ? (
                <TouchableOpacity
                  style={styles.discoverLink}
                  onPress={() => navigation.navigate('VoiceRooms')}
                  accessibilityRole="button"
                  accessibilityLabel="See all voice rooms"
                >
                  <Ionicons name="mic-outline" size={16} color={colors.primary} />
                  <Text style={styles.discoverLinkText}>All voice rooms</Text>
                </TouchableOpacity>
              ) : null
            }
            refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={colors.primary} />}
            showsVerticalScrollIndicator={false}
            contentContainerStyle={[styles.listContent, containerStyle]}
          />
        )
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.bg,
  },
  // Floating translucent chrome stack (skill §12). Padded below status bar.
  chromeStack: {
    paddingHorizontal: spacing.lg,
    paddingBottom: spacing.sm,
  },
  header: {
    flexDirection: 'row',
    // Taller gradient ScreenHeading on the left: align to the top so the compose
    // button doesn't vertically mis-center against the kicker + title block.
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
  },
  headerHeading: {
    flexShrink: 1,
  },
  headerCompose: {
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.primary + '14',
  },
  // "You: " prefix on my own last-message previews (WhatsApp convention).
  previewYou: {
    color: colors.ink2,
    ...fonts.medium,
  },
  segmentGlass: {
    marginTop: spacing.xs,
  },
  // Inner padding of the glass pill (surface). The active pill sits inside this.
  segmentTrack: {
    padding: 3,
  },
  segmentInner: {
    flexDirection: 'row',
    position: 'relative',
  },
  // The sliding active pill (absolute, animated via reanimated spring).
  segmentActivePill: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    backgroundColor: colors.primary,
    borderRadius: radius.full,
  },
  segmentTab: {
    flex: 1,
    paddingVertical: spacing.sm,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: radius.full,
  },
  segmentText: {
    ...typographyScale.caption,
    fontSize: 13,
    ...fonts.semibold,
    color: colors.ink2,
  },
  segmentTextActive: {
    color: colors.white,
  },
  searchGlass: {
    marginTop: spacing.sm,
  },
  searchWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    gap: spacing.sm,
  },
  // Soft fade where the scrolling list meets the floating chrome (skill §12).
  scrollEdgeFade: {
    height: 12,
    marginTop: -1,
    zIndex: 1,
  },
  searchInput: {
    flex: 1,
    ...typographyScale.body,
    fontSize: 14,
    color: colors.ink,
    padding: 0,
  },
  convoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.md,
    gap: spacing.md,
  },
  convoAvatar: {
    width: 56,
    height: 56,
    borderRadius: 28,
  },
  convoAvatarFallback: {
    backgroundColor: colors.primary + '18',
    alignItems: 'center',
    justifyContent: 'center',
  },
  // Unread emphasis: a 2px primary ring around the avatar (WhatsApp-style).
  avatarRing: {
    borderWidth: 2,
    borderColor: colors.primary,
    borderRadius: 30,
    padding: 2,
  },
  // Live presence dot, bottom-right of the DM avatar (skill §7 anchored).
  onlineDot: {
    position: 'absolute',
    bottom: 0,
    right: 0,
    width: 15,
    height: 15,
    borderRadius: 7.5,
    backgroundColor: colors.success,
    borderWidth: 2.5,
    borderColor: colors.bg,
  },
  convoTyping: {
    color: colors.primary,
    fontWeight: '600',
  },
  convoAvatarLetter: {
    fontSize: 18,
    fontWeight: '700',
    color: colors.primary,
  },
  convoContent: {
    flex: 1,
  },
  convoTopRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  convoNameWrap: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: spacing.sm,
  },
  convoName: {
    ...typographyScale.body,
    ...fonts.semibold,
    color: colors.ink,
    flexShrink: 1,
    writingDirection: 'ltr',
  },
  convoNameUnread: {
    ...fonts.bold,
  },
  verifiedIcon: {
    marginLeft: 4,
  },
  convoTime: {
    ...typographyScale.caption,
    color: colors.muted,
  },
  convoTimeUnread: {
    color: colors.primary,
    ...fonts.semibold,
  },
  convoBottomRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: spacing.xs / 2,
  },
  convoMessage: {
    ...typographyScale.caption,
    fontSize: 13,
    lineHeight: 18,
    color: colors.muted,
    flex: 1,
    marginRight: spacing.sm,
    writingDirection: 'ltr',
  },
  convoMessageUnread: {
    color: colors.ink2,
    ...fonts.medium,
  },
  previewLockRow: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: spacing.sm,
    gap: 4,
  },
  previewLockText: {
    flex: 1,
    marginRight: 0,
  },
  unreadBadge: {
    backgroundColor: colors.primary,
    borderRadius: radius.full,
    minWidth: 22,
    height: 22,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 6,
    ...shadowTokens.bar,
  },
  unreadText: {
    ...typographyScale.caption,
    fontSize: 12,
    ...fonts.bold,
    color: colors.white,
    ...tabularNums,
  },
  // WhatsApp inset hairline between DM rows (aligns to text, not the avatar).
  rowSeparator: {
    height: 1,
    marginLeft: 56 + spacing.lg + spacing.md,
    backgroundColor: 'rgba(0,0,0,0.05)',
  },
  listContent: {
    // Clear the floating glass tab bar (A2) so the last row isn't hidden.
    paddingBottom: layoutTokens.bottomNavInset,
    flexGrow: 1,
  },
  loadingWrap: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  // Inline DM-list error + Retry (mirrors DMScreen's error view).
  errorWrap: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.md,
    padding: spacing.xl,
  },
  errorText: {
    fontSize: 15,
    color: colors.ink2,
    textAlign: 'center',
    ...fonts.regular,
  },
  retryBtn: {
    paddingHorizontal: spacing.xl,
    paddingVertical: spacing.md,
    backgroundColor: colors.primary,
    borderRadius: radius.md,
  },
  retryText: {
    fontSize: 15,
    color: colors.white,
    ...fonts.semibold,
  },
  emptyWrap: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: 60,
    paddingHorizontal: spacing.xl,
  },
  emptyCard: {
    width: '100%',
    maxWidth: 340,
    alignItems: 'center',
  },
  emptyCta: {
    marginTop: spacing.lg,
    alignSelf: 'center',
  },
  emptyText: {
    ...typographyScale.h3,
    color: colors.ink,
    textAlign: 'center',
  },
  emptySubtext: {
    ...typographyScale.caption,
    fontSize: 13,
    color: colors.muted,
    textAlign: 'center',
    marginTop: spacing.xs,
  },
  liveBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.error + '20',
    paddingHorizontal: spacing.sm,
    paddingVertical: 2,
    borderRadius: radius.full,
    gap: 4,
  },
  liveDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: colors.error,
  },
  liveText: {
    fontSize: 10,
    ...fonts.bold,
    color: colors.error,
  },
  discoverLink: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.xs,
    paddingVertical: spacing.lg,
  },
  discoverLinkTight: {
    paddingTop: spacing.sm,
  },
  discoverLinkText: {
    ...typographyScale.body,
    fontSize: 14,
    ...fonts.semibold,
    color: colors.primary,
  },
});
